import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { COLOR_CONFLICT_FLAG, TOAST } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { formatDateTime, getLicensePolicyResult, formatDateForRequest } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb, updateDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useMutationData } from "../../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

// Import new components
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import AddImportComponentModal from "@components/UI/AddImportComponentModal";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import PolicyConflictModal from "@components/UI/PolicyConflictModal";
import DuplicateWarningModal from "@components/UI/DuplicateWarningModal";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import StatusBadge from "@components/UI/StatusBadge";
import { extractErrorCode } from "@utils/error";

function ImportedComponentList() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { hasEditPermission } = useContext(PermissionContext);
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();

  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.IMPORTED_COMPONENT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [importedComponentList, setImportedComponentList] = useState([]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModalDelete, setShowModalDelete] = useState(false);
    const [showAddImportComponentModal, setShowAddImportComponentModal] = useState(false);
  const [globalPolicy, setGlobalPolicy] = useState({});
  const [isImportedComponentListLoading, setIsImportedComponentListLoading] = useState(false);
  const { creatorList, importComponentStatusList, fetchSearchInfo } = useSearchInfo();
  const [selectedLicensePolicyResult, setSelectedLicensePolicyResult] = useState(null);
  const [isPolicyConflictModalOpen, setIsPolicyConflictModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [validationError, setValidationError] = useState(null);
  const [showDuplicateWarningModal, setShowDuplicateWarningModal] = useState(false);
  const [duplicateImportComponentSeq, setDuplicateImportComponentSeq] = useState(null);

  const appliedRuleOptions = [
    { value: "Y", label: t("project.imported-components.allow") },
    { value: "N", label: t("project.imported-components.block") },
  ]

  // Advanced Search Fields Configuration
  const advancedSearchFields = [
    {
      name: "componentGroup",
      label: "Component Group",
      type: "text",
      placeholder: t("project.imported-components.component-group-placeholder"),
    },
    {
      name: "componentLicense",
      label: "Component License",
      type: "text",
      placeholder: t("project.imported-components.component-license-placeholder"),
    },
    {
      name: "importComponentStatusCd",
      label: "Component Status",
      type: "select",
      placeholder: t("project.imported-components.component-status-placeholder"),
      options: importComponentStatusList,
    },
     {
      name: "allowFlag",
      label: "Applied Rule",
      type: "select",
      placeholder: t("project.imported-components.nexus-status"),
      options: appliedRuleOptions,
    },
    {
      name: "updaterId",
      label: "LastModified By",
      type: "select",
      placeholder: t("common.last-modified-by"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "updatedAtFrom",
      placeholder: t("project.imported-components.last-modified-from-placeholder"),
      type: "date",
    },
    {
      name: "updatedAtTo",
      placeholder: t("project.imported-components.last-modified-to-placeholder"),
      type: "date",
      minDate: "updatedAtFrom", // Prevent selecting date before updatedAtFrom
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const columns = [
    "licensePolicyConflictFlag",
    "component",
    "componentGroup",
    "realFileName",
    "importComponentStatusCdName",
    "updaterName",
    "updatedAt",
    "allowFlag",
  ];

  const columnConfig = {
    licensePolicyConflictFlag: {
      headerRender: () => t("project.imported-components.license-conflict"),
      customRender: (value, row) => {
        // If not scanned, show "---"
        if (row?.scannedFlag === null || row?.scannedFlag === undefined || row?.scannedFlag === "N") {
          return <div className={`text-sm center font-medium ${colors.textMessage}`}>---</div>;
        }

        const hasConflict = row?.licensePolicyConflictFlag === "Y";
        const backgroundColor = hasConflict ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
        
        return (
          <div className="flex items-center justify-center w-full">
            <div className="flex items-center gap-x-0.5">
              <div
                className={`w-8 h-8 rounded flex items-center justify-center text-base font-bold rounded-l-sm ${hasConflict ? "cursor-pointer" : ""}`}
                style={{ backgroundColor }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasConflict) {
                    const licensePolicyResult = getLicensePolicyResult(globalPolicy, row?.licenseAttribute);
                    setSelectedLicensePolicyResult(licensePolicyResult);
                    setIsPolicyConflictModalOpen(true);
                  }
                }}
              >
                <span className={`${colors.textButton} ${hasConflict ? colors.textStaticWhite : colors.textConflictUndefined }`}>L</span>
              </div>
            </div>
          </div>
        );
      },
      attributeTitle: false,
      style: { width: "7%", textAlign: "center" },
    },

    component: {
      style: {
        width: "17%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => <ComponentHeader/>,
      customRender: (value, row) => (
        <div>
          <div 
            className="text-sm truncate"
            title={`${value || "---"} (${row.componentVersion})`}
          >
            {value || "---"} ({row.componentVersion})
          </div>
          <div 
            className={`text-xs ${colors.textNeutralMuted} truncate`}
            title={row.componentLicense || "---"}
          >
            {row.componentLicense || "---"}
          </div>
        </div>
      ),
    },

    componentGroup: {
      style: {
        width: "12%",
      },
      headerRender: () => t("project.imported-components.componentGroup"),
      customRender: (value, row) => <div className="truncate">{row.componentGroup || "---"}</div>,
    },

    realFileName: {
      style: { width: "15%" },
      headerRender: () => t("project.imported-components.real-file-name"),
      customRender: (value, row) => (
        <div className={`font-normal ${colors.textMessage} truncate`}>{row.realFileName || "---"}</div>
      ),
    },

    importComponentStatusCdName: {
      style: { width: "12%", whiteSpace: "normal", verticalAlign: "middle" },
      headerRender: () => t("project.imported-components.component-status"),
      customRender: (value, row) => {
        const statusConfig = {
          I: { color: "#01BBFE", text: "Imported" }, // Bright blue
          D: { color: "#969696", text: "Disabled" }, // Gray
          A: { color: "#00B649", text: "Approved" }, // Green
          O: { color: "#FDC101", text: "On scanning" }, // Yellow-orange
          S: { color: "#FF9501", text: "Scanned" }, // Orange
          P: { color: "#C084FF", text: "Pending" }, // Purple
        };

        const status = statusConfig[row?.importComponentStatusCd] || { color: "#9CA3AF", text: value || "---" };

        return (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
            <div
              className={`truncate text-[0.8125rem] font-semibold`}
              style={{ color: status.color }}
              title={status.text}
            >
              {status.text}
            </div>
          </div>
        );
      },
    },

    updaterName: {
      style: { width: "12%"},
      headerRender: () => t("common.last-modified-by"),
      customRender: (value, row) => (
        <UserNameWithId name={row.updaterName} id={row.updaterId} />
      ),
    },

    updatedAt: {
      style: { width: "12%"},
      headerRender: () => t("common.last-modified-date"),
      customRender: (value, row) => formatDateTime(row.updatedAt),
    },

    allowFlag: {
      style: { width: "13%", verticalAlign: "middle", whiteSpace: "normal" },
      headerRender: () => t("project.imported-components.nexus-status"),
      customRender: (value, row) => {
        const allowFlag = row?.allowFlag;
        if (allowFlag === null)
          return <div className="text-xs">---</div>;
        return <StatusBadge isAllowed={allowFlag === "Y"} t={t} />;
      },
    },
  };

  // API
  const licensePoliciesData = useMutationData(
    queryKeys.getListLicensePolicy,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (response != "string") {
          const policy = response.find((policy) => policy.globalFlag === "Y");
          setGlobalPolicy(policy);
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const sortByLicensePolicyConflictFlag = useCallback((components, order) => {
    const getPriority = (component) => {
      const conflictFlag = String(component?.licensePolicyConflictFlag || "").toUpperCase().trim();
      const scannedFlag = String(component?.scannedFlag || "").toUpperCase().trim();
      
      if (order === "asc") {
        // Ascending: N/N (1), N/Y (2), Y/Y (3)
        if (conflictFlag === "N" && scannedFlag === "N") return 1;
        if (conflictFlag === "N" && scannedFlag === "Y") return 2;
        if (conflictFlag === "Y" && scannedFlag === "Y") return 3;
        return 4; // Other combinations
      } else {
        // Descending: Y/Y (1), N/Y (2), N/N (3)
        if (conflictFlag === "Y" && scannedFlag === "Y") return 1;
        if (conflictFlag === "N" && scannedFlag === "Y") return 2;
        if (conflictFlag === "N" && scannedFlag === "N") return 3;
        return 4; // Other combinations
      }
    };

    return [...components].sort((a, b) => {
      const aPriority = getPriority(a);
      const bPriority = getPriority(b);
      return aPriority - bPriority;
    });
  }, []);

  const getListData = async (bodyRequest = {}) => {
    setIsImportedComponentListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      // Map sort column to database field name
      let dbSortKey = sortColumn || "createdAt";
      const dbSortOrder = sortDirection || "desc";

      // Special handling for licensePolicyConflictFlag sorting
      if (dbSortKey === "licensePolicyConflictFlag") {
        // Fetch all filtered data without pagination for license policy conflict flag sorting
        const allDataResponse = await getIndexDBData(STORES.IMPORT_COMPONENTS, {
          filters,
          sortKey: null, // Don't sort in IndexedDB, we'll sort in component
          sortOrder: null,
          // Don't pass pageSize to get all filtered results
        });

        if (allDataResponse && typeof allDataResponse !== "string") {
          const allComponents = allDataResponse.object || [];

          // Sort by license policy conflict flag
          const sortedComponents = sortByLicensePolicyConflictFlag(allComponents, dbSortOrder);

          // Apply pagination after sorting
          const totalCount = allDataResponse.objectCount || sortedComponents.length;
          const start = Math.max(startRow, 0);
          const end = start + pageSize;
          const paginatedComponents = sortedComponents.slice(start, end);

          setImportedComponentList(paginatedComponents);
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
        }
      } else {
        // Normal sorting for other columns
        const response = await getIndexDBData(STORES.IMPORT_COMPONENTS, {
          pageSize,
          startRow,
          filters,
          sortKey: dbSortKey,
          sortOrder: dbSortOrder,
        });

        if (response && typeof response !== "string") {
          setImportedComponentList(response.object || []);
          const totalCount = response.objectCount || 0;
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
        }
      }
    }
    catch (error) {
      console.error("Error fetching imported components from IndexedDB:", error);
    } finally {
      setIsImportedComponentListLoading(false);
    }
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  const addImportComponent = useMutationData(
    queryKeys.updateImportComponent,
    {
      method: "POST"
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.IMPORT_COMPONENTS, response, response.importComponentSeq);
        handleModalAddClose();
        if (currentPage === 1) {
          fetchData();
        } else {
          setCurrentPage(1);
        }
        setSelectedRows([]);
        ToastConstant(TOAST.success, t("toast.imported-components.add-success"));
      },
      onError: (error) => {
        console.error("Error Import Component from Nexus:", error);
        if (error?.response && error?.response?.data) {
          const errorData = error?.response?.data;
          
          // Parse error data - can be string or object
          let errorMessage = "";
          let importComponentSeq = null;
          
          if (typeof errorData === "string") {
            errorMessage = errorData;
          } else if (typeof errorData === "object") {
            errorMessage = errorData.message || errorData.toString();
            importComponentSeq = errorData.importComponentSeq || null;
          }
          
          // Determine error type
          const getErrorType = (data) => {
            if (data.includes("Component was already blocked")) return "COMPONENT_ALREADY_BLOCKED";
            if (data.includes("Component already imported.")) return "COMPONENT_ALREADY_IMPORTED";
            if (data.includes("The Nexus host is invalid")) return "NEXUS_HOST_INVALID";
            if (data.includes("The Nexus download URL must contain '/repository/'")) return "NEXUS_URL_MUST_CONTAIN_REPOSITORY";
            if (data.includes("does not exist in Nexus")) return "REPOSITORY_DOES_NOT_EXIST";
            if (data.includes("Download URL Invalid.")) return "DOWNLOAD_URL_INVALID";
            return "UNKNOWN_ERROR";
          };

          const errorType = getErrorType(errorMessage);

          switch (errorType) {
            case "COMPONENT_ALREADY_BLOCKED":
              handleModalAddClose();
              ToastConstant(
                TOAST.error,
                t("project.imported-components.download-component-error", { error: error.message || "Unknown error" }),
                t("project.imported-components.component-already-blocked"),
              );
              break;

            case "COMPONENT_ALREADY_IMPORTED":
              handleModalAddClose();
              // Show duplicate warning modal if importComponentSeq is available
              if (importComponentSeq) {
                setDuplicateImportComponentSeq(importComponentSeq);
                setShowDuplicateWarningModal(true);
              } else {
                // Fallback to toast if no seq available
                ToastConstant(
                  TOAST.error,
                  t("project.imported-components.download-component-error", { error: error.message || "Unknown error" }),
                  t("project.imported-components.component-already-imported"),
                );
              }
              break;

            case "NEXUS_HOST_INVALID":
              const hostMatch = errorMessage.match(/The correct host is: (.+)/);
              if (hostMatch) {
                setValidationError(t("project.imported-components.nexus-host-invalid-with-host", { host: hostMatch[1] }));
              } else {
                setValidationError(t("project.imported-components.nexus-host-invalid"));
              }
              break;

            case "NEXUS_URL_MUST_CONTAIN_REPOSITORY":
              setValidationError(t("project.imported-components.nexus-url-must-contain-repository"));
              break;

            case "REPOSITORY_DOES_NOT_EXIST":
              const match = errorMessage.match(/Repository '([^']+)' does not exist in Nexus/);
              if (match) {
                setValidationError(t("project.imported-components.repository-does-not-exist", { repository: match[1] }));
              } else {
                setValidationError(t("project.imported-components.repository-does-not-exist", { repository: "repository" }));
              }
              break;

            case "DOWNLOAD_URL_INVALID":
              setValidationError(t("project.imported-components.download-component-error-url"));
              break;

            default:
              ToastConstant(
                TOAST.error,
                t("project.imported-components.download-component-error", { error: error.message || "Unknown error" }),
                typeof errorData === "string" ? errorData : errorMessage,
              );
              break;
          }
        }
      },
    },
  );

  const deleteData = useMutationData(
    queryKeys.deleteImport,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.IMPORT_COMPONENTS, selectedRows);
        ToastConstant(TOAST.success, t("toast.imported-components.delete-success"));
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        const errorCode = extractErrorCode(error, "toast.imported-components.delete-error");
        ToastConstant(TOAST.error, t("toast.imported-components.delete-error-title"), t(errorCode));
      },
    },
  );

  // Handler
  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleModalAddOpen = () => {
    setValidationError(null);
    setShowAddImportComponentModal(true);
  };

  const handleModalAddClose = () => {
    setValidationError(null);
    setShowAddImportComponentModal(false);
  };

  const handleAddImportComponent = async (formData) => {
    setValidationError(null); // Clear validation error when submitting
    try {
      await addImportComponent.mutateAsync(formData);
      // Modal will close on success via navigation in onSuccess callback
    } catch (error) {
      // Error is handled in onError callback
      // Keep modal open on error so user can retry
    }
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.importComponentSeq}`);
  };

  const handleDuplicateWarningClose = () => {
    setShowDuplicateWarningModal(false);
    setDuplicateImportComponentSeq(null);
  };

  const handleGoToDuplicateComponent = (importComponentSeq) => {
    setShowDuplicateWarningModal(false);
    setDuplicateImportComponentSeq(null);
    navigate(`./${importComponentSeq}`);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleModalDeleteShow = () => {
    setShowModalDelete(true);
  };

  const handleModalDeleteClose = () => {
    setShowModalDelete(false);
  };

  const onDelete = async () => {
    await deleteData.mutateAsync(selectedRows);
    handleModalDeleteClose();
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  // Hook
  const isFirstRender = useRef(true);

  useEffect(() => {
    const fetchData = async () => {
      await licensePoliciesData.mutateAsync();
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, sortColumn, sortDirection]);

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters, itemsPerPage]);

  const isNonDeleteable = (row) => {
    return row.allowFlag === "N";
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="imported-component-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="imported-component-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid="imported-component-list-title">
            {t("project.imported-components.import-title")}
          </h1>
        </div>
      </div>
      {/* Header with search and controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          controlsTestId="imported-component-list-table-header-controls"
          searchInputTestId="imported-component-list-search-input"
          addButtonTestId="imported-component-list-add-button"
          deleteButtonTestId="imported-component-list-delete-button"
          advancedSearchButtonTestId="imported-component-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          isAdvancedPanelOpen={showAdvancedSearch}
          onDeleteProject={handleModalDeleteShow}
          onAdd={handleModalAddOpen}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("project.imported-components.add-import-component")}
          titleDeleteButton={t("project.imported-components.delete-component")}
          isDisplayAddButton={hasEditPermission}
          isDisplayDeleteButton={hasDeletePermission}
          isSearching={isImportedComponentListLoading}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="imported-component-list-advanced-search">
          <AdvancedSearch
            data-testid="imported-component-list-advanced-search-panel"
          title={t("project.advancedSearch")}
          searchFields={advancedSearchFields}
          searchHandler={searchHandler}
          withoutSearch={true}
          gridCols="md:grid-cols-2 lg:grid-cols-3"
        />
        </div>
      )}

      {/* Table */}
      <div className="flex-grow min-h-0" data-testid="imported-component-list-table">
        <TableWithPagination
          columns={columns}
          data={importedComponentList}
          onRowClick={onRowClick}
          maxRowsPerPage={itemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          totalPages={totalPages}
          totalData={totalData}
          hasCheckbox={hasDeletePermission}
          selectedRows={selectedRows}
          columnConfig={columnConfig}
          tableStyle="default"
          onCheckboxChange={handleCheckboxChange}
          isCheckboxDisabled={isNonDeleteable}
          rowKey="importComponentSeq"
          loading={isImportedComponentListLoading}
          colSize={12}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable={true}
        />
      </div>

      {/* Add Import Component Modal */}
      <AddImportComponentModal
        isOpen={showAddImportComponentModal}
        onClose={handleModalAddClose}
        onSubmit={handleAddImportComponent}
        isLoading={addImportComponent.isPending}
        validationError={validationError}
        onClearValidationError={() => setValidationError(null)}
      />

      {/* Delete confirm modal */}
      <ConfirmDeleteModal
        data-testid="imported-component-list-delete-modal"
        isOpen={showModalDelete}
        onClose={handleModalDeleteClose}
        onConfirm={onDelete}
        title={t("project.imported-components.delete-confirmation")}
        description={t("project.imported-components.delete-imported-component-description")}
        cancelText={t("common.cancel-button")}
        confirmText={t("common.delete-button")}
      />
      <PolicyConflictModal
        isOpen={isPolicyConflictModalOpen}
        onClose={() => {
          setIsPolicyConflictModalOpen(false);
          setSelectedLicensePolicyResult(null);
        }}
        licensePolicyResult={selectedLicensePolicyResult}
      />

      {/* Duplicate Warning Modal */}
      <DuplicateWarningModal
        isOpen={showDuplicateWarningModal}
        onClose={handleDuplicateWarningClose}
        onGoToComponent={handleGoToDuplicateComponent}
        importComponentSeq={duplicateImportComponentSeq}
        title={t("project.imported-components.duplicate-warning-title")}
        description={t("project.imported-components.duplicate-warning-message")}
      />
    </div>
  );
}

export default ImportedComponentList;
